// eslint-disable-next-line import/no-unresolved
/* eslint-disable no-restricted-syntax */

import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Rating } from 'primereact/rating';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { ProgressBar } from 'primereact/progressbar';
import { MultiSelect } from 'primereact/multiselect';

import { products, customer } from '@constants/dummy';
import { CustomerService } from './customService';

const TableDemo = () => {
  const [customer1] = useState(customer);
  const [customer2] = useState(customer);
  const [customer3] = useState(customer);
  const [selectedCustomers, setSelectedCustomers] = useState(null);
  const [globalFilter1, setGlobalFilter1] = useState('');
  const [globalFilter2, setGlobalFilter2] = useState('');
  const [loading1, setLoading1] = useState(true);
  const [loading2, setLoading2] = useState(true);
  const [expandedRows, setExpandedRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [customers, setCustomers] = useState(null);
  const [selectedRepresentative, setSelectedRepresentative] = useState(null);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 0,
  });

  const toast = useRef(null);
  const dt = useRef(null);

  useEffect(() => {
    setTimeout(() => {
      setLoading1(false);
    }, 1000);
    setTimeout(() => {
      setLoading2(false);
    }, 1500);
  }, []);

  const onRowExpand = (event) => {
    toast.current.show({
      severity: 'info',
      summary: 'Product Expanded',
      detail: event.data.name,
      life: 3000,
    });
  };

  const onRowCollapse = (event) => {
    toast.current.show({
      severity: 'success',
      summary: 'Product Collapsed',
      detail: event.data.name,
      life: 3000,
    });
  };

  const expandAll = () => {
    const _expandedRows = {};
    // eslint-disable-next-line no-return-assign
    products.forEach((p) => (_expandedRows[`${p.id}`] = true));

    setExpandedRows(_expandedRows);
    toast.current.show({
      severity: 'success',
      summary: 'All Rows Expanded',
      life: 3000,
    });
  };

  const collapseAll = () => {
    setExpandedRows(null);
    toast.current.show({
      severity: 'success',
      summary: 'All Rows Collapsed',
      life: 3000,
    });
  };

  const formatCurrency = (value) =>
    value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  const calculateCustomerTotal = (name) => {
    let total = 0;

    if (customer3) {
      for (const customerV of customer3) {
        if (customerV.representative.name === name) {
          total += total;
        }
      }
    }

    return total;
  };

  const customer1TableHeader = (
    <div className="table-header">
      List of Customers
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilter1}
          onChange={(e) => setGlobalFilter1(e.target.value)}
          placeholder="Global Search"
        />
      </span>
    </div>
  );

  const customer2TableHeader = (
    <div className="table-header">
      List of Customers
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilter2}
          onChange={(e) => setGlobalFilter2(e.target.value)}
          placeholder="Global Search"
        />
      </span>
    </div>
  );

  const bodyTemplate = (data, props) => (
    <>
      <span className="p-column-title">{props.header}</span>
      {data[props.field]}
    </>
  );

  const countryBodyTemplate = (data) => (
    <>
      <span className="p-column-title">Country</span>
      <img
        src="assets/demo/images/flags/flag_placeholder.png"
        alt={data.country.name}
        className={`flag flag-${data.country.code}`}
        width={30}
        height={20}
      />
      <span
        style={{ marginLeft: '.5em', verticalAlign: 'middle' }}
        className="image-text"
      >
        {data.country.name}
      </span>
    </>
  );

  const representativeBodyTemplate = (data) => (
    <>
      <span className="p-column-title">Representative</span>
      <img
        alt={data.representative.name}
        src={`assets/demo/images/avatar/${data.representative.image}`}
        width="32"
        style={{ verticalAlign: 'middle' }}
      />
      <span
        style={{ marginLeft: '.5em', verticalAlign: 'middle' }}
        className="image-text"
      >
        {data.representative.name}
      </span>
    </>
  );

  const statusBodyTemplate = (data) => (
    <>
      <span className="p-column-title">Status</span>
      <span className={`customer-badge status-${data.status}`}>
        {data.status}
      </span>
    </>
  );

  const activityBody = (data) => (
    <>
      <span className="p-column-title">Activity</span>
      <ProgressBar value={data.activity} showValue={false} />
    </>
  );

  const actionTemplate = () => (
    <Button type="button" icon="pi pi-cog" className="p-button-secondary" />
  );

  const productsTableHeader = (
    <div className="table-header-container">
      <Button
        icon="pi pi-plus"
        label="Expand All"
        onClick={expandAll}
        className="p-mr-2"
      />
      <Button icon="pi pi-minus" label="Collapse All" onClick={collapseAll} />
    </div>
  );

  const imageBodyTemplate = (data) => (
    <>
      <span className="p-column-title">Image</span>
      <img
        src={`assets/demo/images/product/${data.image}`}
        alt={data.image}
        className="product-image"
      />
    </>
  );

  const priceBodyTemplate = (data) => (
    <>
      <span className="p-column-title">Price</span>
      {formatCurrency(data.price)}
    </>
  );

  const reviewsBodyTemplate = (data) => (
    <>
      <span className="p-column-title">Reviews</span>
      <Rating value={data.rating} readonly cancel={false} />
    </>
  );

  const productStatusBodyTemplate = (data) => (
    <>
      <span className="p-column-title">Status</span>
      <span
        className={`product-badge status-${data.inventoryStatus.toLowerCase()}`}
      >
        {data.inventoryStatus}
      </span>
    </>
  );

  const rowExpansionTemplate = (data) => (
    <div className="orders-subtable">
      <h5>Orders for {data.name}</h5>
      <DataTable value={data.orders}>
        <Column field="id" header="Id" sortable body={bodyTemplate} />
        <Column
          field="customer"
          header="Customer"
          sortable
          body={bodyTemplate}
        />
        <Column field="date" header="Date" sortable body={bodyTemplate} />
        <Column field="amount" header="Amount" sortable body={bodyTemplate} />
        <Column
          field="status"
          header="Status"
          sortable
          body={statusBodyTemplate}
        />
        <Column
          headerStyle={{ width: '4rem' }}
          body={() => <Button icon="pi pi-search" />}
        />
      </DataTable>
    </div>
  );

  const headerRowGroup = (data) => (
    <>
      <img
        alt={data.representative.name}
        src={`assets/demo/images/avatar/${data.representative.image}`}
        width="32"
        style={{ verticalAlign: 'middle' }}
      />
      <span
        style={{ marginLeft: '.5em', verticalAlign: 'middle' }}
        className="image-text"
      >
        {data.representative.name}
      </span>
    </>
  );

  const footerRowGroup = (data) => (
    <>
      <td colSpan="4" style={{ textAlign: 'right' }}>
        <strong>Total Customers:</strong>
      </td>
      <td>
        <strong>{calculateCustomerTotal(data.representative.name)}</strong>
      </td>
    </>
  );

  const representatives = [
    { name: 'Amy Elsner', image: 'amyelsner.png' },
    { name: 'Anna Fali', image: 'annafali.png' },
    { name: 'Asiya Javayant', image: 'asiyajavayant.png' },
    { name: 'Bernardo Dominic', image: 'bernardodominic.png' },
    { name: 'Elwin Sharvill', image: 'elwinsharvill.png' },
    { name: 'Ioni Bowcher', image: 'ionibowcher.png' },
    { name: 'Ivan Magalhaes', image: 'ivanmagalhaes.png' },
    { name: 'Onyama Limba', image: 'onyamalimba.png' },
    { name: 'Stephen Shaw', image: 'stephenshaw.png' },
    { name: 'XuXue Feng', image: 'xuxuefeng.png' },
  ];

  const customerService = new CustomerService();

  let loadLazyTimeout = null;

  useEffect(() => {
    loadLazyData();
  }, [lazyParams]);

  const loadLazyData = () => {
    setLoading(true);

    if (loadLazyTimeout) {
      clearTimeout(loadLazyTimeout);
    }
    console.log('lazyParams-----', lazyParams);
    // imitate delay of a backend call
    loadLazyTimeout = setTimeout(() => {
      customerService
        .getCustomers({ lazyEvent: JSON.stringify(lazyParams) })
        .then((data) => {
          setTotalRecords(data.totalRecords);
          setCustomers(data.customers);
          setLoading(false);
        });
    }, Math.random() * 1000 + 250);
  };

  const onPage = (event) => {
    console.log('event onPage', event);
    const _lazyParams = { ...lazyParams, ...event };
    setLazyParams(_lazyParams);
  };

  const onSort = (event) => {
    console.log('event onSort', event);
    const _lazyParams = { ...lazyParams, ...event };
    setLazyParams(_lazyParams);
  };

  const onFilter = (event) => {
    console.log('event onFilter', event);
    const _lazyParams = { ...lazyParams, ...event };
    _lazyParams.first = 0;
    setLazyParams(_lazyParams);
  };

  const representativeBodyTemplateN = (rowData) => (
    <>
      <img
        alt={rowData.representative.name}
        src={`showcase/demo/images/avatar/${rowData.representative.image}`}
        onError={(e) =>
          (e.target.src =
            'https://www.primefaces.org/wp-content/uploads/2020/05/placeholder.png')
        }
        width={32}
        style={{ verticalAlign: 'middle' }}
      />
      <span className="image-text">{rowData.representative.name}</span>
    </>
  );

  const representativesItemTemplate = (option) => (
    <div className="p-multiselect-representative-option">
      <img
        alt={option.name}
        src={`showcase/demo/images/avatar/${option.image}`}
        onError={(e) =>
          (e.target.src =
            'https://www.primefaces.org/wp-content/uploads/2020/05/placeholder.png')
        }
        width={32}
        style={{ verticalAlign: 'middle' }}
      />
      <span className="image-text">{option.name}</span>
    </div>
  );

  const onRepresentativesChange = (e) => {
    dt.current.filter(e.value, 'representative.name', 'in');
    setSelectedRepresentative(e.value);
  };

  const countryBodyTemplateN = (rowData) => (
    <>
      <img
        alt="flag"
        src="showcase/demo/images/flag_placeholder.png"
        onError={(e) =>
          (e.target.src =
            'https://www.primefaces.org/wp-content/uploads/2020/05/placeholder.png')
        }
        className={`flag flag-${rowData.country.code}`}
        width={30}
      />
      <span className="image-text">{rowData.country.name}</span>
    </>
  );

  const representativeFilter = (
    <MultiSelect
      value={selectedRepresentative}
      options={representatives}
      filter
      itemTemplate={representativesItemTemplate}
      onChange={onRepresentativesChange}
      optionLabel="name"
      optionValue="name"
      placeholder="All"
      className="p-column-filter"
    />
  );

  return (
    <div className="p-grid table-demo">
      <div className="p-col-12">
        <div className="card">
          <h5>Default</h5>
          <p>Pagination, sorting, filtering and checkbox selection.</p>
          <DataTable
            value={customer1}
            paginator
            className="p-datatable-customers"
            rows={10}
            dataKey="id"
            rowHover
            selection={selectedCustomers}
            onSelectionChange={(e) => setSelectedCustomers(e.value)}
            globalFilter={globalFilter1}
            emptyMessage="No customers found."
            loading={loading1}
            header={customer1TableHeader}
          >
            <Column selectionMode="multiple" headerStyle={{ width: '3em' }} />
            <Column field="name" header="Name" sortable body={bodyTemplate} />
            <Column
              field="country.name"
              header="Country"
              sortable
              body={countryBodyTemplate}
            />
            <Column
              field="representative.name"
              header="Representative"
              sortable
              body={representativeBodyTemplate}
            />
            <Column field="date" header="Date" sortable body={bodyTemplate} />
            <Column
              field="status"
              header="Status"
              sortable
              body={statusBodyTemplate}
            />
            <Column
              field="activity"
              header="Activity"
              sortable
              body={activityBody}
            />
            <Column
              headerStyle={{ width: '8rem', textAlign: 'center' }}
              bodyStyle={{
                textAlign: 'center',
                overflow: 'visible',
                justifyContent: 'center',
              }}
              body={actionTemplate}
            />
          </DataTable>
        </div>
      </div>
      <div className="p-col-12">
        <div className="card">
          <h5>Customized</h5>
          <p>
            Scrollable table with gridlines (<mark>.p-datatable-gridlines</mark>
            ), striped rows (<mark>.p-datatable-striped</mark>) and smaller
            paddings (<mark>p-datatable-sm</mark>).
          </p>
          <DataTable
            value={customer2}
            scrollable
            scrollHeight="600px"
            className="p-datatable-gridlines p-datatable-striped p-datatable-sm p-datatable-customers"
            dataKey="id"
            rowHover
            globalFilter={globalFilter2}
            emptyMessage="No customers found."
            loading={loading2}
            header={customer2TableHeader}
          >
            <Column field="name" header="Name" sortable body={bodyTemplate} />
            <Column
              field="country.name"
              header="Country"
              sortable
              body={countryBodyTemplate}
            />
            <Column
              field="representative.name"
              header="Representative"
              sortable
              body={representativeBodyTemplate}
            />
            <Column field="date" header="Date" sortable body={bodyTemplate} />
            <Column
              field="status"
              header="Status"
              sortable
              body={statusBodyTemplate}
            />
            <Column
              field="activity"
              header="Activity"
              sortable
              body={activityBody}
            />
          </DataTable>
        </div>
      </div>

      <div className="p-col-12">
        <div className="card">
          <h5>Row Expand</h5>

          <Toast ref={toast} />
          <DataTable
            value={products}
            expandedRows={expandedRows}
            className="p-datatable-customers"
            dataKey="id"
            onRowToggle={(e) => setExpandedRows(e.data)}
            onRowExpand={onRowExpand}
            onRowCollapse={onRowCollapse}
            header={productsTableHeader}
            rowExpansionTemplate={rowExpansionTemplate}
          >
            <Column expander headerStyle={{ width: '3rem' }} />
            <Column field="name" header="Name" sortable body={bodyTemplate} />
            <Column header="Image" body={imageBodyTemplate} />
            <Column
              field="price"
              header="Price"
              sortable
              body={priceBodyTemplate}
            />
            <Column
              field="category"
              header="Category"
              sortable
              body={bodyTemplate}
            />
            <Column
              field="rating"
              header="Reviews"
              sortable
              body={reviewsBodyTemplate}
            />
            <Column
              field="inventoryStatus"
              header="Status"
              sortable
              body={productStatusBodyTemplate}
            />
          </DataTable>
        </div>
      </div>

      <div className="p-col-12">
        <div className="card">
          <h5>Row Group</h5>
          <DataTable
            value={customer}
            rowGroupMode="subheader"
            className="p-datatable-customers"
            groupField="representative.name"
            sortMode="single"
            sortField="representative.name"
            sortOrder={1}
            rowGroupHeaderTemplate={headerRowGroup}
            rowGroupFooterTemplate={footerRowGroup}
          >
            <Column field="representative.name" header="Representative" />
            <Column field="name" header="Name" body={bodyTemplate} />
            <Column
              field="country"
              header="Country"
              body={countryBodyTemplate}
            />
            <Column field="company" header="Company" body={bodyTemplate} />
            <Column field="status" header="Status" body={statusBodyTemplate} />
            <Column field="date" header="Date" body={bodyTemplate} />
          </DataTable>
        </div>
      </div>

      <div className="p-col-12">
        <div className="card">
          <h5>Lazy</h5>
          <DataTable
            ref={dt}
            value={customers}
            lazy
            paginator
            first={lazyParams.first}
            rows={10}
            totalRecords={totalRecords}
            onPage={onPage}
            onSort={onSort}
            sortField={lazyParams.sortField}
            sortOrder={lazyParams.sortOrder}
            onFilter={onFilter}
            filters={lazyParams.filters}
            loading={loading}
          >
            <Column
              field="name"
              header="Name"
              sortable
              filter
              filterPlaceholder="Search by name"
            />
            <Column
              field="country.name"
              sortable
              header="Country"
              filterField="country.name"
              body={countryBodyTemplateN}
              filter
              filterPlaceholder="Search by country"
              filterMatchMode="contains"
            />
            <Column
              field="company"
              sortable
              filter
              header="Company"
              filterPlaceholder="Search by company"
            />
            <Column
              field="representative.name"
              header="Representative"
              body={representativeBodyTemplateN}
              filter
              filterElement={representativeFilter}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default TableDemo;
